import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PluginListenerHandle } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { BehaviorSubject, distinctUntilChanged, filter, fromEvent, Observable, skip } from 'rxjs';
import { AppStateChangeService } from './app-state-change.service';
import { PlatformService } from './platform.service';
import { SubscriptionsBag } from '@utils/subscriptions-bag';

@Injectable({
  providedIn: 'root'
})
export class OnlineStateService implements OnDestroy {
  private readonly appStateChangeService = inject(AppStateChangeService);
  private readonly platformService = inject(PlatformService);
  private readonly ngZone = inject(NgZone);

  private readonly isOfflineSubject = new BehaviorSubject<boolean>(
    this.platformService.isBrowser && typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  public readonly isOffline$: Observable<boolean> = this.isOfflineSubject.asObservable();
  public readonly reconnected$ = this.isOffline$.pipe(distinctUntilChanged(), skip(1), filter(isOffline => !isOffline));
  private readonly offlineState = toSignal(this.isOffline$, {requireSync: true});

  private readonly sb = new SubscriptionsBag();
  private useNativeNetwork = this.platformService.isNativePlatform;
  private networkListener: PluginListenerHandle | null = null;
  private networkStatusVersion = 0;
  private destroyed = false;

  public get isOffline(): boolean {
    return this.offlineState();
  }

  constructor() {
    if (this.platformService.isSSR) return;

    if (this.useNativeNetwork) {
      void this.subscribeToNativeNetworkEvents();
    } else {
      this.subscribeToWindowEvents();
    }
    this.subscribeToAppState();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.sb.unsubscribeAll();
    void this.networkListener?.remove().catch(error => console.warn('Cannot remove network listener', error));
    this.networkListener = null;
  }

  private async subscribeToNativeNetworkEvents(): Promise<void> {
    try {
      const listener = await Network.addListener('networkStatusChange', status => {
        this.networkStatusVersion++;
        this.updateOfflineState(!status.connected);
      });
      if (this.destroyed) {
        await listener.remove();
        return;
      }
      this.networkListener = listener;
      await this.refreshNativeNetworkStatus();
    } catch (error) {
      if (this.destroyed) return;
      console.warn('Cannot monitor native network state', error);
      this.useNativeNetwork = false;
      this.subscribeToWindowEvents();
      this.refreshBrowserNetworkStatus();
    }
  }

  private async refreshNativeNetworkStatus(): Promise<void> {
    const version = ++this.networkStatusVersion;
    try {
      const status = await Network.getStatus();
      if (this.destroyed || version !== this.networkStatusVersion) return;
      this.updateOfflineState(!status.connected);
    } catch (error) {
      if (!this.destroyed) console.warn('Cannot read native network state', error);
    }
  }

  private subscribeToWindowEvents(): void {
    if (typeof window === 'undefined') return;

    this.sb.sub = fromEvent(window, 'online').subscribe((): void => {
      this.updateOfflineState(false);
    });

    this.sb.sub = fromEvent(window, 'offline').subscribe((): void => {
      this.updateOfflineState(true);
    });
  }

  private subscribeToAppState(): void {
    this.sb.sub = this.appStateChangeService.appStateChange$.pipe(
      distinctUntilChanged(),
      filter((isActive: boolean): boolean => isActive),
    ).subscribe((): void => {
      if (this.useNativeNetwork) {
        if (this.networkListener) void this.refreshNativeNetworkStatus();
      } else {
        this.refreshBrowserNetworkStatus();
      }
    });
  }

  private refreshBrowserNetworkStatus(): void {
    this.updateOfflineState(this.platformService.isBrowser && typeof navigator !== 'undefined' && navigator.onLine === false);
  }

  private updateOfflineState(isOffline: boolean): void {
    if (this.destroyed || this.isOfflineSubject.value === isOffline) return;
    this.ngZone.run((): void => this.isOfflineSubject.next(isOffline));
  }
}
